import { useQuery } from "@tanstack/react-query";
import { apiClient, assertJsonObject } from "src/lib/apiClient";
import { keys, staleTimes } from "src/queries/keys";

/**
 * Display pricing from GET /pricing/:countryCode — the same table the
 * backend charges from, so the advertised price can never drift from the
 * charged price.
 */
export interface DisplayPricing {
  country_code: string | null;
  /** False when the country has no active CountryPricing row — registration blocked. */
  available: boolean;
  /** True when the country's subscription price is zero (free plan). */
  is_free: boolean;
  currency: string;
  symbol: string;
  cost: number;
  formatted: string;
  referral_applied: boolean;
  /** ZAR per one unit of the local currency; null if FX is unavailable. */
  zar_per_unit: number | null;
  /** Digital flyer unit pricing (ZAR). */
  flyer_zar_cost_per_user: number;
  flyer_zar_minimum_cost: number;
}

/**
 * Eight components mount this hook, several of them on the same screen. Before
 * it was a query that meant eight concurrent identical requests; now they share
 * one cache entry per (countryCode, referralCode).
 *
 * The return shape is deliberately unchanged from the pre-TanStack version so
 * no call site needed touching. Failures resolve to `pricing: null` rather than
 * throwing, as before — callers treat missing pricing as "don't show a price",
 * never as a hard error.
 */
export const usePricing = (countryCode?: string, referralCode?: string) => {
  const code = countryCode?.trim();

  const { data, isPending, isError } = useQuery({
    queryKey: keys.reference.pricing(code, referralCode),
    enabled: !!code,
    staleTime: staleTimes.reference,
    queryFn: async () => {
      const response = await apiClient.get(
        `pricing/${encodeURIComponent(code!)}`,
        { params: referralCode ? { referralCode } : undefined }
      );
      // A misconfigured API base URL makes this a same-origin request that the
      // SPA fallback answers with index.html and a 200 — accept only a real
      // pricing object.
      return assertJsonObject<DisplayPricing>(response.data, "/pricing");
    },
  });

  return {
    pricing: data ?? null,
    // With no country code there is nothing to load, matching the old behaviour
    // of returning loading:false immediately rather than spinning forever.
    loading: !!code && isPending && !isError,
  };
};
